import * as React from "react";
import { PrintSettingsElementEnum } from "@app/access-reports/admin";
import { flatten, isEqual } from "lodash";
import { ColorResult, SketchPicker } from "react-color";
import { Modal } from "@app/shared/Modals";
import { JSX } from "react";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import { AccessReportsPrintOptions } from "@app/access-reports/shared/accessReportsPrintOptions";
import { AccessReportsWizardElement } from "@app/access-reports/shared/accessReportsWizardElement";
import { AccessReportsWizardElementTypeEnum } from "@app/access-reports/shared/accessReportsWizardElementTypeEnum";
import { AccessReportsWizardFormGroup } from "@app/access-reports/shared/accessReportsWizardFormGroup";
import { AccessReportsWizardModel } from "@app/access-reports/shared/accessReportsWizardModel";
import { AccessReportsWizardPage } from "@app/access-reports/shared/accessReportsWizardPage";
import { AccessReportsWizardStepEnum } from "@app/access-reports/shared/accessReportsWizardStepEnum";
import { AccessReportsWizardTemplate } from "@app/access-reports/shared/accessReportsWizardTemplate";
import { AccessReportsWizardVersionModel } from "@app/access-reports/shared/accessReportsWizardVersionModel";

interface AccessReportsAdminPrintSettingsTabProps {
  accessReportsWizard: AccessReportsWizardModel;
  disableUserActions: boolean;
  onPrintOptionsChange: (formGroupId: number, key: keyof AccessReportsPrintOptions, defaultValue: string | boolean) => void;
  onResetPrintSettingsClick: () => void;
  onSaveClick: () => void;
  selectedVersion: AccessReportsWizardVersionModel;
}

const AccessReportsAdminPrintSettingsTab: React.FC<AccessReportsAdminPrintSettingsTabProps> = (props: AccessReportsAdminPrintSettingsTabProps) => {
  const defaultsButtonRef = React.useRef<HTMLButtonElement>(null);
  const [displayColorPicker, setDisplayColorPicker] = React.useState<PrintSettingsElementEnum>(null);
  const [displayResetAlert, setDisplayResetAlert] = React.useState(false);

  const builtPrintOptionsSection = () => {
    const printOptionsPage: AccessReportsWizardPage = props.selectedVersion.template.pages.find((x) => x.title === AccessReportsWizardStepEnum.PrintOptions);

    return (
      <div className="tab-pane fade" id="print-settings">
        {displayResetAlert && (
          <Modal
            closeModal={() => hideResetAlert(false)}
            content={{
              body: "Are you sure you want to restore the default values for all print settings?",
              cancelButtonText: "Cancel",
              confirmButtonAction: () => hideResetAlert(true),
              confirmButtonText: "Reset",
              title: "Warning",
            }}
            isVisible={displayResetAlert}
            hideBodyScroll={true}
            triggerElement={defaultsButtonRef}
          />
        )}

        <div className="row">
          <div className="col-12">
            <div className="d-flex justify-content-end mb-3">
              <QESButton
                disabled={disableSave(printOptionsPage)}
                qesType="primary"
                qesTestId={"access-reports-admin__dashboard--print-settings-save"}
                onClick={() => props.onSaveClick()}
              >
                Save
              </QESButton>
            </div>
          </div>
        </div>

        <div className="row">
          {printOptionsPage.formGroups.map((formGroup, formGroupIndex) => (
            <div
              className={`col-md-4${formGroupIndex !== 0 ? " ps-5" : ""}${(formGroupIndex !== printOptionsPage.formGroups.length - 1) ? " pe-5 access-print-options-divider" : ""}`}
              key={formGroupIndex}
            >
              {formGroup.elements.map((element, elementIndex) => (
                <div className="row mb-3" key={elementIndex}>
                  {buildFormElement(printOptionsPage, formGroup, element)}
                </div>
              ))}
            </div>
          ))}
        </div>

        <div className="row">
          <div className="col-12">
            <div className="d-flex justify-content-end mb-3">
              <QESButton
                disabled={disableReset(printOptionsPage)}
                qesType="primary"
                qesTestId={"access-reports-admin__dashboard--print-settings-reset"}
                onClick={() => showResetAlert()}
                qesRef={defaultsButtonRef}
              >
                Restore Defaults
              </QESButton>
            </div>

          </div>
        </div>
      </div>
    );
  }

  const buildFormElement = (
    printOptionsPage: AccessReportsWizardPage,
    formGroup: AccessReportsWizardFormGroup,
    element: AccessReportsWizardElement
  ): JSX.Element => {
    const disabled: boolean = getDisabledStatus(printOptionsPage, element);

    switch (element.elementType) {
      case AccessReportsWizardElementTypeEnum.Checkbox:
        return (
          <React.Fragment>
            <div className="offset-md-5 col-md-7">
              <QESCheckbox
                checked={(element.defaultValue !== undefined && element.defaultValue !== null) ? element.defaultValue as boolean : false}
                disabled={disabled}
                id={`access-reports-admin__print-settings--${element.elementId}-checkbox`}
                onChange={() => props.onPrintOptionsChange(formGroup.id, element.elementId as keyof AccessReportsPrintOptions, !element.defaultValue)}
                label={element.title}
                testId={`access-reports-print-settings__${element.elementId}`}
              />
            </div>
          </React.Fragment>
        );
      case AccessReportsWizardElementTypeEnum.ColorPicker:
        const presetColors: string[] = element.options.map(x => x.color as string);

        return (
          <React.Fragment>
            <label className="col-md-5 text-start col-form-label">{element.title}</label>

            <div className="col-md-7 d-flex align-items-center">
              <div className="color-picker-swatch" onClick={() => onColorPickerClick(element.elementId as PrintSettingsElementEnum)}>
                <div className="color-picker-color" style={{ backgroundColor: element.defaultValue as string || "#000" }} />
              </div>

              {(displayColorPicker === element.elementId) &&
                <div className="color-picker-popover">
                  <div
                    className="color-picker-cover"
                    onClick={() => onColorPickerClose()}
                  />

                  <SketchPicker
                    color={element.defaultValue as string || "#000"}
                    disableAlpha={true}
                    presetColors={presetColors}
                    onChange={onColorPickerChange}
                  />
                </div>
              }
            </div>
          </React.Fragment>
        );
      case AccessReportsWizardElementTypeEnum.Dropdown:
        return (
          <React.Fragment>
            <label className="col-md-5 text-start col-form-label">{element.title}</label>

            <div className="col-md-7">
              <QESSelect
                className="form-control"
                disabled={disabled}
                testId={`access-reports-admin__print-settings--${element.elementId}-dropdown`}
                onChange={(e) => props.onPrintOptionsChange(formGroup.id, element.elementId as keyof AccessReportsPrintOptions, e.target.value)}
                value={(element.defaultValue !== undefined && element.defaultValue !== null) ? element.defaultValue.toString() : ""}
                label=""
              >
                <option key="<none>" value="" />
                {element.options && element.options.map((option, optionIndex) => (
                  <option
                    key={optionIndex}
                    value={option.id}
                  >
                    {option.displayName ? option.displayName : option.name}
                  </option>
                ))}
              </QESSelect>
            </div>
          </React.Fragment>
        );
      case AccessReportsWizardElementTypeEnum.Input:
        return (
          <React.Fragment>
            <label className="col-md-5 text-start col-form-label">{element.title}</label>

            <div className="col-md-7">
              <input
                className="form-control"
                disabled={disabled}
                id={`access-reports-admin__print-settings--${element.elementId}-input`}
                onChange={(e) => props.onPrintOptionsChange(formGroup.id, element.elementId as keyof AccessReportsPrintOptions, e.target.value)}
                value={(element.defaultValue !== undefined && element.defaultValue !== null) ? element.defaultValue.toString() : ""}
              />
            </div>
          </React.Fragment>
        );
      case AccessReportsWizardElementTypeEnum.DatePicker:
        return (
          <div className="offset-md-5 col-md-7">
            <input
              className="form-control"
              disabled={disabled}
              id={`access-reports-admin__print-settings--${element.elementId}-datepicker`}
              onChange={(event) => props.onPrintOptionsChange(formGroup.id, element.elementId as keyof AccessReportsPrintOptions, event.target.value)}
              type="datetime-local"
              value={(element.defaultValue !== undefined && element.defaultValue !== null) ? element.defaultValue.toString() : ""}
            />
          </div>
        );
      default:
        break;
    }
  }

  const disableReset = (inProgressPrintOptionsPage: AccessReportsWizardPage): boolean => {
    if (!props.disableUserActions) {
      const printOptionsElements: AccessReportsWizardElement[] = flatten(inProgressPrintOptionsPage.formGroups.map(x => x.elements));

      for (const element of printOptionsElements) {
        if (element.defaultValue !== element.originalDefaultValue) {
          return false;
        }
      }
    }

    return true;
  }

  const disableSave = (inProgressPrintOptionsPage: AccessReportsWizardPage): boolean => {
    const currentlySavedTemplate: AccessReportsWizardTemplate = props.accessReportsWizard.accessReportWizardVersions.find(
      x => x.accessReportWizardVersionId === props.selectedVersion.accessReportWizardVersionId
    )?.template;
    const currentlySavedPrintOptions: AccessReportsWizardPage = currentlySavedTemplate.pages.find((x) => x.title === AccessReportsWizardStepEnum.PrintOptions);

    if (isEqual(currentlySavedPrintOptions, inProgressPrintOptionsPage) || props.disableUserActions) {
      return true;
    }

    return false;
  }

  const showResetAlert = (): void => {
    setDisplayResetAlert(true);
  }

  const getDisabledStatus = (
    printOptionsPage: AccessReportsWizardPage,
    element: AccessReportsWizardElement
  ): boolean => {
    if (props.disableUserActions) {
      return true;
    } else {
      if (element.disabled) {
        // if the element is defined as disabled by the template
        return true;
      } else {
        if (element.previousRequiredElementId) {
          // if the element's accessibility depends on a previous required element
          for (const formGroup of printOptionsPage.formGroups) {
            for (const e of formGroup.elements) {
              if (e.elementId === element.previousRequiredElementId) {
                // if the previous required element must have a specific value
                if (element.previousRequiredValueId !== null && element.previousRequiredValueId !== undefined) {
                  if (e.defaultValue.toString() !== element.previousRequiredValueId.toString()) {
                    return true;
                  }
                } else {
                  // if the previous required element does not need a specific value,
                  // but a value of some sort does indeed need to be present
                  if (e.defaultValue === null || e.defaultValue === undefined || e.defaultValue === false) {
                    return true;
                  }
                }
              }
            }
          }
        }
      }
    }

    return false;
  }

  const hideResetAlert = (hideResetConfirmed: boolean): void => {
    if (hideResetConfirmed) {
      props.onResetPrintSettingsClick();
    }

    setDisplayResetAlert(false);
  }

  const onColorPickerClick = (printSettingsElementId: PrintSettingsElementEnum): void => {
    if (printSettingsElementId === displayColorPicker) {
      setDisplayColorPicker(null);
    } else {
      setDisplayColorPicker(printSettingsElementId);
    }
  }

  const onColorPickerChange = (color: ColorResult): void => {
    const printOptionsPage: AccessReportsWizardPage = props.selectedVersion.template.pages.find((x) => x.title === AccessReportsWizardStepEnum.PrintOptions);
    let formGroupId: number = null;

    for (const formGroup of printOptionsPage.formGroups) {
      const element = formGroup.elements.find(x => x.elementId === displayColorPicker)

      if (!!element) {
        formGroupId = formGroup.id;
        props.onPrintOptionsChange(formGroupId, displayColorPicker, color.hex);

        break;
      }
    }
  }

  const onColorPickerClose = (): void => {
    setDisplayColorPicker(null);
  }

  return builtPrintOptionsSection();
}

export { AccessReportsAdminPrintSettingsTab };
